/**
 * useComposeDraft Hook
 * Manages auto-save draft logic for compose dialog
 */

import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { DELAYS } from '@/constants/timing';
import { useMessageDrafts } from '@/hooks/useMessageDrafts';
import { useAuth } from '@/contexts/AuthContext';
import type { ComposeFormData, Expert } from '../types';

interface UseComposeDraftOptions {
  formData: ComposeFormData;
  selectedExperts: Expert[];
  isOpen: boolean;
}

export function useComposeDraft({ formData, selectedExperts, isOpen }: UseComposeDraftOptions) {
  const { user } = useAuth();
  const { saveDraftAsync, isSaving: isDraftSaving } = useMessageDrafts();
  
  const [currentDraftId, setCurrentDraftId] = useState<string | null>(null);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const autoSaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Use refs for values that shouldn't trigger callback recreation
  const formDataRef = useRef(formData);
  const selectedExpertsRef = useRef(selectedExperts);

  // Update refs when values change
  useEffect(() => {
    formDataRef.current = formData;
    selectedExpertsRef.current = selectedExperts;
  }, [formData, selectedExperts]);

  // Memoize strings for dependency comparison (stable primitives)
  const formDataString = useMemo(() => JSON.stringify(formData), [formData]);
  const expertIds = useMemo(() => selectedExperts.map(e => e.id).join(','), [selectedExperts]);

  const triggerAutoSave = useCallback(() => {
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }
    
    const currentFormData = formDataRef.current;
    const currentExperts = selectedExpertsRef.current;
    
    // Skip auto-save for unauthenticated guests
    if (!user) return;
    
    // Only save draft if there's a valid subject
    const hasValidSubject = currentFormData.subject && currentFormData.subject.trim().length > 0;
    if (!hasValidSubject) return;
    
    autoSaveTimeoutRef.current = setTimeout(async () => {
      try {
        const result = await saveDraftAsync({
          draftId: currentDraftId || undefined,
          formData: {
            recipient_ids: currentExperts.map(e => e.id),
            subject: currentFormData.subject,
            message: currentFormData.message,
            service_type: currentFormData.service_type,
            location: currentFormData.location,
            budget_range: currentFormData.budget_range,
            timeline: currentFormData.timeline,
          },
        });
        if (!currentDraftId && result?.id) {
          setCurrentDraftId(result.id);
        }
        setLastSavedAt(new Date());
      } catch (error) {
        // Log in development for debugging
        if (process.env.NODE_ENV === 'development') {
          console.warn('[useComposeDraft] Auto-save failed:', error);
        }
        // Silent fail in production - don't interrupt user
      }
    }, DELAYS.TOAST_SHORT);
  }, [currentDraftId, saveDraftAsync, user]); // Stable dependencies only

  // Trigger auto-save on form changes using serialized strings for comparison
  useEffect(() => {
    if (isOpen) {
      triggerAutoSave();
    }
    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, [formDataString, expertIds, isOpen, triggerAutoSave]);

  const resetDraft = useCallback(() => {
    setCurrentDraftId(null);
    setLastSavedAt(null);
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }
  }, []);

  return {
    currentDraftId,
    lastSavedAt,
    isDraftSaving,
    resetDraft,
  };
}
